<template>
  <section>
    <header>
      <h1 class="page-header">Sign In</h1>
    </header>
    <errors-alert
      v-bind:errors="errorsFromBackend"
      v-on:clear="clearAlert()"
    />
    <section class="alert alert-info" v-if="keepMeSignedIn && isSafari">
      You are using Safari. If you want to remain logged in after closing the tab, you'll have to enable cross-site tracking: Safari > Preferences > Privacy > Website tracking.
    </section>
    <section class="alert alert-info" v-if="previousSignInType">
      Last time you were here you used <strong>{{ previousSignInType }}</strong> to sign in<span v-if="previousSignInType === 'email'"> (as opposed to google, twitter or linkedin)</span>.
    </section>
    <form>
      <section class="form-group">
        <label>
          Email
          <input v-model="email" type="email" class="form-control input-lg" autofocus>
        </label>
      </section>
      <section class="form-group">
        <label>
          Password
          <input v-model="password" v-bind:type="passwordFieldType" class="form-control input-lg">
        </label>
        <span class="help-block">
          <p v-if="daysSincePasswordChange">{{ daysSincePasswordChangeText }}</p>
          <div class="checkbox">
            <label>
              <input v-model="showPassword" type="checkbox"> Show password
            </label>
          </div>
        </span>
      </section>
      <button
        class="btn btn-primary btn-lg btn-block"
        v-bind:disabled="signingIn"
        v-on:click.prevent="submit()"
      >
        {{ signInText }}
      </button>
      <div class="checkbox">
        <label>
          <input v-model="keepMeSignedIn" type="checkbox" class="keep-me-signed-in"> Keep me signed in on this computer
        </label>
      </div>
    </form>
    <p><router-link to="/forgot-password">Forgot your password?</router-link></p>
    <hr>
    <p>
      <a class="btn btn-social btn-google btn-lg btn-block" v-bind:href="googleSignInUrl" v-bind:disabled="signingIn">
        <i class="fab fa-google"></i>
        Sign in with Google
      </a>
    </p>
    <p>
      <a class="btn btn-social btn-twitter btn-lg btn-block" v-bind:href="twitterSignInUrl" v-bind:disabled="signingIn">
        <i class="fab fa-twitter"></i>
        Sign in with Twitter
      </a>
    </p>
    <p>
      <a class="btn btn-social btn-linkedin btn-lg btn-block" v-bind:href="linkedinSignInUrl" v-bind:disabled="signingIn">
        <i class="fab fa-linkedin"></i>
        Sign in with LinkedIn
      </a>
    </p>
    <hr>
    <p>Don't have an account? <router-link to="/register">Register here</router-link>.</p>
  </section>
</template>

<script>
import ErrorsAlert from '@/components/utilities/errors-alert';
import utilities from '@/services/utilities/utilities';

export default {
  name: 'sign-in',
  components: {
    'errors-alert': ErrorsAlert,
  },
  data: function () {
    return {
      email: '',
      password: '',
      showPassword: false,
      keepMeSignedIn: true,
      errorsFromBackend: [],
      signingIn: false,
    };
  },
  computed: {
    isSafari: function () {
      return /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
    },
    previousSignInType: function () {
      return localStorage.getItem('previousSignInType');
    },
    passwordFieldType: function () {
      if (this.showPassword) {
        return 'text';
      } else {
        return 'password';
      }
    },
    daysSincePasswordChange: function () {
      let changedPasswordDate = localStorage.getItem('changedPasswordDate');
      let daysSincePasswordChange;

      if (!changedPasswordDate) {
        return null;
      }

      daysSincePasswordChange = utilities.general.getDaysBetweenDates(changedPasswordDate, new Date());

      if (daysSincePasswordChange > 7) {
        localStorage.removeItem('changedPasswordDate');
        return null;
      }

      return daysSincePasswordChange;
    },
    daysSincePasswordChangeText: function () {
      if (this.daysSincePasswordChange < 1) {
        return 'Note: You changed your password less than 24 hours ago.';
      }

      if (this.daysSincePasswordChange < 1.5) {
        return `Note: You changed your password yesterday.`;
      }

      return `Note: You changed your password ${Math.round(this.daysSincePasswordChange)} days ago.`;
    },
    googleSignInUrl: function () {
      return `${process.env.BASE_API_URL}/user/google`;
    },
    twitterSignInUrl: function () {
      return `${process.env.BASE_API_URL}/user/twitter`;
    },
    linkedinSignInUrl: function () {
      return `${process.env.BASE_API_URL}/user/linkedin`;
    },
    signInText: function () {
      if (this.signingIn) {
        return 'Signing you in...';
      }

      return 'Sign in';
    },
  },
  created: function () {
    if (this.$route.query.error) {
      if (this.$route.query.error === 'email-in-use') {
        this.errorsFromBackend.push('The email you\'re trying to sign up with is already in  use.');
      }
    }

    if (this.$route.query.error) {
      if (this.$route.query.error === 'not-logged-in') {
        this.errorsFromBackend.push('You must be logged in to access that page.');
      }
    }
  },
  methods: {
    submit: function () {
      let self = this;

      self.signingIn = true;
      self.$http
        .post(`${process.env.BASE_API_URL}/user/login`, {
          user: {
            email: self.email,
            password: self.password,
          },
          keepMeSignedIn: self.keepMeSignedIn,
        })
        .then(function (res) {
          self.signingIn = false;
          self.$store.commit('account/setUser', res.data.user);
          self.$router.push({ path: '/account' });
        })
        .catch(function (err) {
          self.signingIn = false;
          self.errorsFromBackend.splice(0, self.errorsFromBackend.length);
          err.response.data.errors.forEach(function (errorFromBackend) {
            self.errorsFromBackend.push(errorFromBackend);
          });
        })
      ;
    },
    clearAlert: function () {
      this.errorsFromBackend.splice(0, this.errorsFromBackend.length);
    },
  },
};
</script>

<style lang="scss" scoped>
  section, input, button, a {
    font-size: 18px;
  }

  label {
    width: 100%;
  }

  .help-block {
    margin-top: -10px;

    p {
      margin-top: 10px;
    }
  }

  .btn-social {
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/user/sign-in.vue